import { concat, noop } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Box } from '@mui/material';

import ToastRenderer, { Toast } from './toastr-renderer';
import { useMuiSwitchableTheme } from '../../theme';

/**
 * How far the mobile toast stack sits above the bottom edge while a dialog is
 * open: a dialog's action row (8px padding, a ~40px button, 8px padding) plus
 * the gap the stack keeps from whatever is beneath it.
 */
export const TOAST_DIALOG_CLEARANCE_PX = 72;

/**
 * What counts as "an overlay with an action row is open", as selectors on
 * <body> — MUI portals its modals there.
 *
 * `:not(.MuiModal-hidden)` on both: a `keepMounted` modal stays in <body> while
 * closed, carrying that class, and must not hold the stack up.
 *
 * Drawers as well as dialogs: the side sheets (schedule a job, assign a buyer)
 * are temporary Drawers, full width on a phone with their buttons along the
 * bottom. Only `.MuiDrawer-modal` — a docked drawer isn't an overlay — and not
 * the left-anchored one, which is the navigation menu and has no action row.
 */
export const TOAST_OVERLAY_OPEN_SELECTORS = [
  'body:has(> .MuiDialog-root:not(.MuiModal-hidden))',
  'body:has(> .MuiDrawer-modal:not(.MuiModal-hidden) .MuiDrawer-paper:not(.MuiDrawer-paperAnchorLeft))',
];

interface IToastrProviderProps {
  children: ReactNode;
}

interface IToastrContext {
  push: (t: Toast) => void;
  /**
   * Alias for `push` (preferred name in some call sites/tests).
   */
  pushToast: (t: Toast) => void;
  remove: (id: string | number) => void;
}

const ToastrContext = createContext<IToastrContext>({
  push: noop,
  pushToast: noop,
  remove: noop,
});

const useToastr = () => useContext(ToastrContext);

function ToastrProvider({ children }: IToastrProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { marginTop } = useMuiSwitchableTheme();

  const updateToasts = useCallback((toast: Toast) => {
    const toastWithId = {
      ...toast,
      id: toast?.id || Date.now() + Math.random(),
    };
    setToasts((arr) =>
      arr.find((t) => t.id === toast.id)
        ? arr
        : concat(
            // Remove existing toasts marked for removal on next push
            arr.filter((t) => !t.removeOnNextPush),
            toastWithId,
          ),
    );
  }, []);

  const removeToast = useCallback((id: string | number) => {
    setToasts((arr) => arr.filter((toast) => toast.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({
      push: updateToasts,
      pushToast: updateToasts,
      remove: removeToast,
    }),
    [updateToasts, removeToast],
  );

  return (
    <ToastrContext.Provider value={contextValue}>
      <Box
        sx={(theme) => ({
          position: 'fixed',
          zIndex: 9999,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          // Phones anchor the stack to the BOTTOM edge and run it full width;
          // everything sm and up keeps the top-right stack.
          //
          // Top-right is the wrong corner on a phone for two reasons: it is the
          // far end of the screen from the thumb, so dismissing means a reach;
          // and it lands directly under the app header, where a 400px card on a
          // ~390px screen is already effectively full width but with the top
          // 16px of it tucked against the header.
          //
          // Anchoring the bottom rather than the top also means the stack grows
          // UPWARD, so the newest toast (last in the array) is always the one
          // nearest the thumb and older ones move away instead of shifting the
          // new arrival around.
          top: { xs: 'auto', sm: marginTop + 16 },
          // env() keeps the strip off the iPhone home indicator; on a device
          // without one the inset resolves to 0 and this is a plain 16px.
          bottom: {
            xs: 'calc(16px + env(safe-area-inset-bottom))',
            sm: 'auto',
          },
          // 8px matches the app's mobile page gutter, so a full-width toast
          // lines up with the content behind it rather than floating free.
          left: { xs: 8, sm: 'auto' },
          right: { xs: 8, sm: 16 },
          maxWidth: { xs: 'none', sm: '400px' },
          '& > *': {
            pointerEvents: 'auto',
          },
          // ...except while a dialog or sheet is open. Those are full-bleed on a
          // phone and keep their actions in a row along the bottom edge, which
          // is exactly where this strip sits — at zIndex 9999, over Save and
          // Cancel, for as long as the toast lives. A toast raised BY the
          // dialog ("file too large") then blocks the button the user needs
          // next. So the stack rides above the action row instead.
          //
          // They portal to <body>, hence the child combinator. Where :has()
          // isn't supported the rule is simply dropped and the strip stays at
          // the bottom edge — no worse than before. The clearance is one
          // button row; a footer that wraps to two is still partly covered.
          [theme.breakpoints.down('sm')]: {
            [TOAST_OVERLAY_OPEN_SELECTORS.map((sel) => `${sel} &`).join(', ')]:
              {
                bottom: `calc(${TOAST_DIALOG_CLEARANCE_PX}px + env(safe-area-inset-bottom))`,
              },
          },
        })}
      >
        {toasts.map((toast, index) => (
          <Box
            key={toast.id || Date.now()}
            sx={{
              // Stagger animation delays for multiple toasts
              animationDelay: `${index * 100}ms`,
            }}
          >
            <ToastRenderer toast={toast} onRemove={removeToast} />
          </Box>
        ))}
      </Box>
      {children}
    </ToastrContext.Provider>
  );
}

export { ToastrContext, ToastrProvider, useToastr };
export type { IToastrContext, IToastrProviderProps };
